import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { ArrowLeft, Eye, Lock, Save } from 'lucide-react';
import * as React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@/design';
import { LoadError, useAdminData } from '@/domains/admin';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type {
  ContentPostKind,
  ContentPostStatus,
  IContentPost,
  IContentPostBody,
  IContentSeoBody
} from '../api/post-contracts';
import { postsApi } from '../api/posts-api';
import { CONTENT_PATHS } from '../paths';
import { ImagePicker, useImageChooser, useLibrary } from '../library';
import { EditPresenceNotice, useEditPresence } from '../presence';
import { BodyEditor, RenderedBody } from '../rich-text';
import {
  ConflictNotice,
  Field,
  FieldRefusal,
  HistoryPanel,
  SeoFields,
  StatusPill,
  personName,
  slugify
} from '../shared';
import { ContentPage } from './content-page';

/**
 * One post, written (WEB-06, WEB-10, WEB-11, WEB-13, WEB-14, WEB-15), over
 * `/api/v1/admin/website/posts/{postId}`.
 *
 * This is the screen the module exists for — Craig Hall's "keep it as simple as here is my login"
 * — and five requirements land on it at once. Each is drawn where the editor meets it rather than
 * collected into a panel at the bottom, because a warning nobody scrolls to is not a warning.
 *
 * **The form is seeded once per post, by remounting** (`key` on the loader). Seeding from props
 * on mount and never resetting is a real bug this repo has already paid for once: one shared
 * editor instance kept the previous record's values under the new record's heading, and saving
 * would have written one item's content onto another. A remount per id cannot do that.
 *
 * **Concurrency is survey-api's and the `ETag` is opaque here.** The read hands back the post's
 * revision as an `ETag`, every write quotes it in `If-Match`, and the write's answer carries the
 * next one. Nothing on this side parses, compares or increments a revision: a stale one comes
 * back as a 409 `revision-conflict`, drawn as a refusal with no "save anyway" — see
 * `ConflictNotice`. The post's own `revision` member is drawn and never used as a precondition.
 *
 * **Publishing is a permission, not a step.** `content.publish` decides which control the panel
 * draws; `content.author` is what every other write needs, and `AdminRoute` has already refused
 * anybody holding neither. An author without the publish permission gets "Mark as finished",
 * which stamps the record and gates nothing. That is WEB-13's resolution of its own two
 * contradictory sources, and it is why there is no approval queue anywhere on this screen.
 *
 * **The slug stops moving once published.** WEB-06 AC2 asks that a correction not change the URL,
 * so the field follows the title while the post is a draft and is read-only afterwards. The rule
 * is the server's — it refuses a changed address on a live post — and this field only reflects
 * it, because a published URL is cited and a read-only input is not a guarantee.
 *
 * There is no lead image. The media library is a later iteration and survey-api holds no site
 * images yet, so the field is absent rather than present and silently dropped.
 */
export function ContentPostEditorPage() {
  const { postId } = useParams<{ postId: string }>();
  if (!postId) return <Navigate to={CONTENT_PATHS.news} replace />;
  return <PostLoader key={postId} postId={postId} />;
}

function PostLoader({ postId }: { postId: string }) {
  const { data, error, adopt } = useAdminData((signal) => postsApi.read(postId, signal), [postId]);

  if (error) {
    return (
      <ContentPage tabs={false} crumb="News & Insights" title="Post">
        <LoadError message={error} />
      </ContentPage>
    );
  }
  if (!data) return null;
  return <PostEditor post={data.body} etag={data.etag} onWrote={adopt} />;
}

const KINDS: { value: ContentPostKind; label: string }[] = [
  { value: 'news', label: 'News' },
  { value: 'insight', label: 'Insight' },
  { value: 'press_release', label: 'Press release' }
];

/**
 * What the one save control is called, which is not the same question on all three statuses
 * (WEB-06 AC2).
 *
 * It said "Save draft" on every post, including a live one. On a published post the press is
 * what puts the correction in front of readers — the record stays published, the address stays
 * frozen, and no second publication is needed — so the button names that rather than the
 * opposite of it. An author who has just fixed a typo in a live press release should not have
 * to go looking for a publish button that is not there.
 */
const SAVE_LABEL: Record<ContentPostStatus, string> = {
  draft: 'Save draft',
  published: 'Publish the correction',
  // Off the site: a save is an edit to a record no reader can see, and calling it a correction
  // would promise a publication that is not happening.
  archived: 'Save changes'
};

/**
 * What the preview calls itself. It said "not published" on every post, a live one included —
 * on the screen an author opens to check a correction before it goes out (WEB-06 AC2).
 */
const PREVIEW_TITLE: Record<ContentPostStatus, string> = {
  draft: 'Preview — not published',
  published: 'Preview — this post is live',
  archived: 'Preview — off the site'
};

/** The editable half, as the form holds it and a write sends it. */
function bodyOf(post: IContentPost): IContentPostBody {
  return {
    kind: post.kind,
    title: post.title,
    summary: post.summary,
    body: post.body,
    author: post.author,
    publishedOn: post.publishedOn,
    slug: post.slug,
    // The form holds what a write sends — ids — while the record holds what a read answered.
    seo: {
      title: post.seo.title,
      description: post.seo.description,
      imageId: post.seo.image?.imageId ?? null
    },
    leadImageId: post.leadImage?.imageId ?? null
  };
}

function PostEditor({
  post,
  etag,
  onWrote
}: {
  post: IContentPost;
  etag: Nullable<string>;
  /** A write's answer, adopted so the screen redraws from it without a second read. */
  onWrote: (next: { body: IContentPost; etag: Nullable<string> }) => void;
}) {
  const can = useCan();
  const [form, setForm] = React.useState<IContentPostBody>(() => bodyOf(post));
  const [busy, setBusy] = React.useState(false);
  const [conflict, setConflict] = React.useState<Nullable<string>>(null);
  const [refusals, setRefusals] = React.useState<{ target: string; reason: string }[]>([]);
  const [done, setDone] = React.useState<Nullable<{ title: string; detail: string }>>(null);
  const [preview, setPreview] = React.useState(false);
  // The picture the article opens with, resolved from the id the form holds. The library is a
  // shared, cached read — the picker on this screen has already asked for it.
  const { images } = useLibrary();
  const lead = images.find((image) => image.imageId === form.leadImageId) ?? null;
  const { choose: chooseImage, chooser: imageChooser } = useImageChooser();

  const others = useEditPresence('post', post.postId);

  const draft = post.status === 'draft';
  const dirty = JSON.stringify(form) !== JSON.stringify(bodyOf(post));
  const set = <K extends keyof IContentPostBody>(key: K, value: IContentPostBody[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const refusalOf = (target: string) => refusals.find((problem) => problem.target === target);

  /**
   * The address follows the title until the post is published, and then stops for good (AC2).
   *
   * Only once the title changes, which is what `titled` guards — a comparison rather than a
   * first-run flag, because StrictMode runs the effect twice on mount. Deriving on mount would
   * overwrite an address somebody had edited by hand the moment they reopened the draft — and
   * silently, since the field would simply be holding a different value than the one they saved.
   */
  const titled = React.useRef(form.title);
  React.useEffect(() => {
    if (!draft || titled.current === form.title) return;
    titled.current = form.title;
    setForm((current) => ({ ...current, slug: slugify(current.title) }));
  }, [form.title, draft]);

  /**
   * "Load their version" — the only way out of a conflict this screen offers.
   *
   * It re-reads and **reseeds the form**, which is the whole point: reloading the record while
   * leaving the typed text in place would leave the editor saying it had loaded their version
   * while still holding the one that was refused, and the next save would overwrite them after
   * all. Whatever was typed is lost deliberately — WEB-11 AC1 asks that it be reapplied on top
   * of theirs, not merged behind their back.
   */
  const reload = async () => {
    const fresh = await postsApi.read(post.postId);
    onWrote(fresh);
    setForm(bodyOf(fresh.body));
    setConflict(null);
    setRefusals([]);
    setDone(null);
  };

  /**
   * Every write on this screen, once: run it, adopt what it answered, and say what it refused.
   *
   * The refusals are told apart rather than collapsed into one message, because each asks the
   * author for something different — reload and reapply, or fix a named field.
   */
  const write = async (
    run: (precondition: string) => Promise<{ body: IContentPost; etag: Nullable<string> }>,
    said: (answered: IContentPost) => { title: string; detail: string }
  ) => {
    if (!etag) {
      // Only reachable if the header did not arrive: survey-api exposes `ETag` through CORS, and
      // a write without it would be a 428 the author cannot act on.
      setConflict('this page was loaded without its revision');
      return;
    }
    setBusy(true);
    setConflict(null);
    setRefusals([]);
    try {
      const answer = await run(etag);
      onWrote(answer);
      setForm(bodyOf(answer.body));
      setDone(said(answer.body));
    } catch (error) {
      setDone(null);
      if (error instanceof ApiError && error.isProblem(ProblemType.revisionConflict)) {
        setConflict(personName(post.updatedBy));
      } else if (
        error instanceof ApiError &&
        (error.isProblem(ProblemType.invalidBody) || error.isProblem(ProblemType.codeInUse))
      ) {
        setRefusals(error.problem?.problems ?? [{ target: '', reason: 'was refused.' }]);
      } else {
        setRefusals([{ target: '', reason: 'could not be saved. Try again.' }]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ContentPage
      tabs={false}
      crumb="News & Insights"
      title={form.title || 'Untitled post'}
      actions={
        <>
          <Button variant="ghost" size="sm" asChild>
            <Link to={CONTENT_PATHS.news}>
              <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
              All posts
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPreview(true)}>
            <Eye className="mr-1 h-4 w-4" aria-hidden />
            Preview
          </Button>
          <Button
            size="sm"
            onClick={() =>
              void write((precondition) => postsApi.save(post.postId, precondition, form), SAID.saved)
            }
            disabled={!dirty || busy}
          >
            <Save className="mr-1 h-4 w-4" aria-hidden />
            {busy ? 'Saving…' : SAVE_LABEL[post.status]}
          </Button>
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="grid gap-6">
          <EditPresenceNotice kind="post" others={others} />
          {conflict && <ConflictNotice by={conflict} onReload={() => void reload()} />}
          {refusalOf('') && (
            <Alert variant="destructive">
              <AlertTitle>Not saved</AlertTitle>
              <AlertDescription>The post {refusalOf('')!.reason}</AlertDescription>
            </Alert>
          )}
          {done && !conflict && refusals.length === 0 && (
            <Alert>
              <Save className="h-4 w-4" aria-hidden />
              <AlertTitle>{done.title}</AlertTitle>
              <AlertDescription>{done.detail}</AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>The post</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              <Field id="title" label="Title" refusal={refusalOf('title')}>
                <Input id="title" value={form.title} onChange={(e) => set('title', e.target.value)} />
              </Field>

              <Field id="slug" label="Address" refusal={refusalOf('slug')}>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">/news/</span>
                  <Input
                    id="slug"
                    value={form.slug ?? ''}
                    readOnly={!draft}
                    onChange={(e) => set('slug', e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {draft
                    ? 'Follows the title until the post is published, then stays put.'
                    : 'Fixed once published — the address is cited elsewhere.'}
                </p>
              </Field>

              <div className="grid gap-4 sm:grid-cols-3">
                <Field id="kind" label="Kind" refusal={refusalOf('kind')}>
                  <Select
                    value={form.kind}
                    onValueChange={(value) => set('kind', value as ContentPostKind)}
                  >
                    <SelectTrigger id="kind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {KINDS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field id="author" label="By-line" refusal={refusalOf('author')}>
                  <Input
                    id="author"
                    value={form.author}
                    placeholder="The research team"
                    onChange={(e) => set('author', e.target.value)}
                  />
                </Field>
                <Field id="published-on" label="Publication date" refusal={refusalOf('publishedOn')}>
                  <Input
                    id="published-on"
                    type="date"
                    value={form.publishedOn}
                    onChange={(e) => set('publishedOn', e.target.value)}
                  />
                </Field>
              </div>

              <Field id="summary" label="Summary" refusal={refusalOf('summary')}>
                <Textarea
                  id="summary"
                  rows={2}
                  value={form.summary}
                  onChange={(e) => set('summary', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Shown on the news index and used as the description when none is set.
                </p>
              </Field>

              {/* WEB-06 AC3's first half: a picture placed in the words, from the same library
                  the lead image comes from. The editor asks for one through a callback because
                  it lives in the design system, which may not reach into this domain. */}
              <BodyEditor
                label="Body"
                value={form.body}
                onChange={(value) => set('body', value)}
                onPickImage={async () => {
                  const image = await chooseImage();
                  return image ? { url: image.url, alt: image.alt, imageId: image.imageId } : null;
                }}
              />
              {imageChooser}
              {refusalOf('body') && <FieldRefusal reason={refusalOf('body')!.reason} />}

              {/* WEB-09 AC1 — the library, reused across posts. Its own field rather than part
                  of the sharing panel: the lead image is what the article opens with, and the
                  sharing card is a different picture cropped for a different shape. */}
              <ImagePicker
                label="Lead image"
                value={form.leadImageId}
                current={post.leadImage}
                onPick={(imageId) => set('leadImageId', imageId)}
              />
              {refusalOf('leadImageId') && (
                <FieldRefusal reason={refusalOf('leadImageId')!.reason} />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>How this looks when shared</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2">
              <SeoFields
                seo={form.seo}
                onChange={(next: IContentSeoBody) => set('seo', next)}
                pageTitle={`${form.title || 'Untitled post'} | Veritant`}
                socialImageId={form.seo.imageId}
                socialImage={post.seo.image}
                onPickSocialImage={(imageId) => set('seo', { ...form.seo, imageId })}
              />
              {refusalOf('seo.title') && <FieldRefusal reason={refusalOf('seo.title')!.reason} />}
              {refusalOf('seo.description') && (
                <FieldRefusal reason={refusalOf('seo.description')!.reason} />
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 self-start">
          <PublishPanel
            post={post}
            canPublish={can('content.publish')}
            busy={busy}
            onPublish={() => void write((pre) => postsApi.publish(post.postId, pre), SAID.published)}
            onUnpublish={() =>
              void write((pre) => postsApi.unpublish(post.postId, pre), SAID.unpublished)
            }
            onSubmit={() => void write((pre) => postsApi.submit(post.postId, pre), SAID.submitted)}
          />

          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
            </CardHeader>
            <CardContent>
              <HistoryPanel
                history={post.history.map((version) => ({
                  versionNo: version.versionNo,
                  at: version.at,
                  by: personName(version.by),
                  action: version.action,
                  note: version.note
                }))}
                onRevert={(versionNo) =>
                  void write((pre) => postsApi.revert(post.postId, pre, versionNo), SAID.reverted)
                }
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{PREVIEW_TITLE[post.status]}</DialogTitle>
          </DialogHeader>
          {/* It scrolls because a post has no length limit: without the cap the dialog grows past
              the viewport and the end of the article cannot be reached. */}
          <DialogBody as="article" className="max-h-[70dvh] gap-3 overflow-y-auto">
            {/* The lead image is the first thing the article shows on the site, so it is the
                first thing the preview shows. Drawn from the form rather than from the saved
                post, for the reason the body below is: a picture just chosen and not yet saved
                is exactly what an author opens this dialog to check. */}
            {lead && (
              <img
                src={lead.url}
                alt={lead.alt}
                className="aspect-video w-full rounded-md object-cover"
              />
            )}
            <p className="text-xs tracking-wide text-muted-foreground uppercase">
              {KINDS.find((kind) => kind.value === form.kind)?.label} · {form.publishedOn}
            </p>
            <h2 className="text-xl font-light text-[var(--color-royal-blue)]">
              {form.title || 'Untitled post'}
            </h2>
            {form.summary && <p className="text-sm text-muted-foreground">{form.summary}</p>}
            {/* The form's body, not the saved one. Drawing what had last been saved showed an
                author who had just written — or just corrected — an article an empty or stale
                preview, which is the one thing this dialog exists to show, while the title and
                summary above it were already the form's. What survives survey-api's sanitiser is
                known without a round trip: `RenderedBody` sanitises on the way out through
                `rich-text-allowlist.ts`, a mirror of the server's, and the editor writes against
                that same allowlist. */}
            <RenderedBody html={form.body} />
            {dirty && (
              <p className="text-xs text-muted-foreground">
                This is the post as it stands in the editor — some of it is not saved yet.
              </p>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </ContentPage>
  );
}

/**
 * What the editor says after each write landed.
 *
 * One sentence per action rather than one for all of them: a screen that answered "Saved" to a
 * publish — which is what this did until the alert was split — tells an author that nothing went
 * out at the exact moment something did.
 *
 * The publish line is careful about what it claims. The record is published; the public pages are
 * a separate build on a separate stack (WEB-01) and are not wired to this yet, so the sentence
 * says what changed here and does not promise a page.
 */
const SAID = {
  /**
   * A save says what the save did, which depends on what it was saved to (WEB-06 AC2).
   *
   * One sentence for all three statuses is how this screen came to tell a published post's
   * author that "nothing reached the live site" — while the correction they had just made was
   * part of the live post, needing no second publication. That is the exact belief AC2 exists to
   * prevent, and it was the wrong half of the three.
   *
   * The status read here is the **answered** one, not the one the screen opened on, so a save
   * that raced somebody else's publish still describes what is now true.
   */
  saved: (post: IContentPost) => {
    const version = post.history.at(-1)?.versionNo;
    if (post.status === 'published') {
      return {
        title: `Correction published as version ${version}`,
        detail:
          'It is part of the live post — there is no second publication to make, and the '
          + 'address has not changed. The public pages are built separately (WEB-01) and do '
          + 'not read this yet.'
      };
    }
    if (post.status === 'archived') {
      return {
        title: `Saved as version ${version}`,
        detail:
          'The post is off the site, so this reached no reader. Put it back from the panel on '
          + 'the right.'
      };
    }
    return {
      title: `Saved as version ${version}`,
      detail: 'Nothing reached the live site — a draft is published from the panel on the right.'
    };
  },
  published: (post: IContentPost) => ({
    title: 'Published',
    detail:
      `Marked live as version ${post.history.at(-1)?.versionNo}. The public pages are built ` +
      'separately (WEB-01) and do not read this yet.'
  }),
  unpublished: () => ({
    title: 'Taken off the site',
    detail: 'The post and every version of it are kept — nothing here deletes.'
  }),
  submitted: () => ({
    title: 'Marked as finished',
    detail: 'It stays a draft. Whoever holds the publish permission decides when it goes out.'
  }),
  reverted: (post: IContentPost) => ({
    title: post.history.at(-1)?.note ?? 'Restored an earlier version',
    detail: `Appended as version ${post.history.at(-1)?.versionNo} — the history keeps both.`
  })
};

/** A labelled field with the server's refusal of it underneath, where there is one. */

/**
 * Publishing, and the one place the permission is visible (WEB-13 AC1, AC2).
 *
 * An author who cannot publish is not shown a disabled Publish button with a tooltip — they are
 * shown the control they do have, and told that somebody else holds the other one. A disabled
 * control is a standing reminder of a permission you lack.
 *
 * It does not name who can. The mock this replaced listed the editors holding the capability;
 * survey-api answers the caller's own permissions and nothing about anybody else's, and there is
 * no route that would say which accounts hold `content.publish` short of `accounts.browse` —
 * which a marketing author is not going to have.
 */
function PublishPanel({
  post,
  canPublish,
  busy,
  onPublish,
  onUnpublish,
  onSubmit
}: {
  post: IContentPost;
  canPublish: boolean;
  busy: boolean;
  onPublish: () => void;
  onUnpublish: () => void;
  onSubmit: () => void;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle>Publishing</CardTitle>
        <StatusPill status={post.status} />
      </CardHeader>
      <CardContent className="grid gap-3">
        <p className="text-xs text-muted-foreground">
          Version {post.revision} · last change {formatDateTime(post.updatedAt)} by{' '}
          {personName(post.updatedBy)}.
        </p>

        {post.submittedAt && post.status === 'draft' && (
          <p className="text-xs text-[var(--color-teal)]">
            Marked finished {formatDateTime(post.submittedAt)}.
          </p>
        )}

        {canPublish ? (
          <div className="grid gap-2">
            {post.status !== 'published' ? (
              <Button size="sm" onClick={onPublish} disabled={busy}>
                Publish now
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={onUnpublish} disabled={busy}>
                Take off the site
              </Button>
            )}
            <p className="text-xs text-muted-foreground">
              Goes live immediately — there is no approval step behind this button.
            </p>
          </div>
        ) : (
          <div className="grid gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={busy || post.submittedAt !== null || post.status !== 'draft'}
              onClick={onSubmit}
            >
              <Lock className="mr-1 h-4 w-4" aria-hidden />
              {post.submittedAt ? 'Marked as finished' : 'Mark as finished'}
            </Button>
            <p className="text-xs text-muted-foreground">
              You can write and save but not publish. Marking it finished tells whoever can.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
